const text = (value) => String(value ?? '').replace(/\s+/g, ' ').trim();
const normalized = (value) => text(value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[.,]/g, '')
  .toLocaleLowerCase('pt-BR');

const normalizedStreet = (value) => normalized(value)
  .replace(/^(rua|r|avenida|av|travessa|tv|alameda|praça|praca|largo|viela)\s+/, '')
  .replace(/\bluis\b/g, 'luiz')
  .trim();

export const checkoutStreetsMatch = (left, right) => Boolean(
  normalizedStreet(left) && normalizedStreet(left) === normalizedStreet(right),
);

const explicitTrailingHouseNumber = /^(.*?)(?:\s*,\s*|\s+n(?:[º°o]|\.)?\s*)(\d{1,6}[a-zA-Z]?(?:[-/]\w+)?)$/iu;
const separatedHouseNumberAndNeighborhood = /^(.*?)\s*,\s*(\d{1,6}[a-zA-Z]?(?:[-/]\w+)?)(?:\s+-\s+[^,]+)?(?:\s*,\s*.+)?$/iu;
const plainTrailingHouseNumber = /^(.*?)\s+(\d{1,6}[a-zA-Z]?(?:[-/]\w+)?)$/u;
const urbanStreetPrefix = /^(?:rua|r\.?|avenida|av\.?|travessa|tv\.?|alameda|praça|praca|largo|viela)\s+/iu;

export const splitCheckoutStreetAndNumber = (value, suggestedStreet = '') => {
  const input = text(value);
  const explicitMatch = input.match(explicitTrailingHouseNumber);
  const plainMatch = input.match(plainTrailingHouseNumber);
  const match = explicitMatch || input.match(separatedHouseNumberAndNeighborhood) || plainMatch;
  if (!match) return { street: input, number: '' };

  const street = text(match[1]).replace(/,+$/, '').trim();
  const number = text(match[2]);
  if (!street || !number) return { street: input, number: '' };
  if (!explicitMatch && !urbanStreetPrefix.test(street)) return { street: input, number: '' };

  const suggestion = text(suggestedStreet);
  if (suggestion && normalizedStreet(street) !== normalizedStreet(suggestion)) {
    return { street: input, number: '' };
  }

  return { street: suggestion || street, number };
};

// Separa somente o padrão explícito ("Rua X, 123" ou "Rua X n 123"), seguro
// sem sugestão confirmada: a vírgula ou o marcador "n" desambiguam "Rua 13".
export const extractExplicitStreetNumber = (value) => {
  const input = text(value);
  const match = input.match(explicitTrailingHouseNumber);
  if (!match) return { street: input, number: '' };
  const street = text(match[1]).replace(/,+$/, '').trim();
  const number = text(match[2]);
  if (!street || !number) return { street: input, number: '' };
  return { street, number };
};

export const completeCheckoutGeographicAddress = (address = {}, { allowMissingState = false } = {}) => (
  (allowMissingState ? ['street', 'number', 'neighborhood', 'city'] : ['street', 'number', 'neighborhood', 'city', 'state'])
    .every((field) => text(address[field]).length > 0)
);

export const checkoutAutomaticSuggestionKey = (typedStreet = '') => normalizedStreet(typedStreet);

// Auto-adocao da sugestao de rua (mesmo contrato do Cardapio): quando o
// operador digita o logradouro completo e nao clica na lista, aplica somente a
// coincidencia exata do nome normalizado, uma vez por rua digitada. Nunca
// adota prefixo ("Rua Par" -> "Rua Parana") e preserva o numero digitado junto.
export const chooseAutomaticCheckoutStreetSuggestion = ({ typedStreet = '', items = [], appliedKey = '', limit = 6, searchComplete = false } = {}) => {
  const query = text(typedStreet);
  if (query.length < 3) return null;
  const key = checkoutAutomaticSuggestionKey(query);
  if (!key || key === appliedKey || !searchComplete) return null;
  const list = Array.isArray(items) ? items : [];
  if (list.length >= limit) return null;
  const matches = list.filter((item) => {
    const street = text(item?.street || item?.address?.street);
    if (!street) return false;
    if (checkoutStreetsMatch(query, street)) return true;
    const typed = splitCheckoutStreetAndNumber(query, street);
    return Boolean(typed.number) && checkoutStreetsMatch(typed.street, street);
  });
  if (!matches.length) return null;
  const identity = (item) => [item?.street || item?.address?.street,
    item?.neighborhood || item?.address?.neighborhood, item?.city || item?.address?.city,
    item?.state || item?.address?.state, item?.postalCode || item?.address?.postalCode || item?.address?.cep]
    .map(normalized).join('|');
  if (matches.some((item) => !text(item?.neighborhood || item?.address?.neighborhood)
    || !text(item?.city || item?.address?.city) || !text(item?.state || item?.address?.state)
    || !text(item?.postalCode || item?.address?.postalCode || item?.address?.cep)
    || item?.postalVerified !== true || item?.autoSelectable !== true)) return null;
  return new Set(matches.map(identity)).size === 1 ? { suggestion: matches[0], key } : null;
};

export const checkoutAutomaticPreviewKey = (address = {}) => JSON.stringify(
  ['street', 'number', 'neighborhood', 'city', 'state', 'cep']
    .map((field) => normalized(address[field])),
);

export const checkoutAutomaticPreviewPlan = ({ enabled, mapOpen, selection, address, allowMissingState = false, hasPlaceSelection = false }, previousKey = '') => {
  // A selected place may lack a street/number/neighborhood. This only starts
  // its signed server verification; it never authorizes quote/order creation.
  const canVerifyPlace = hasPlaceSelection && text(address?.city) && text(address?.state);
  if (!enabled || mapOpen || selection || (!canVerifyPlace && !completeCheckoutGeographicAddress(address, { allowMissingState }))) {
    return { shouldStart: false, key: '' };
  }
  const key = checkoutAutomaticPreviewKey(address);
  return { shouldStart: Boolean(key && key !== previousKey), key };
};

export const checkoutAutomaticPreviewResponseIsCurrent = ({ requestId, currentRequestId, key, currentKey }) => (
  Number(requestId) === Number(currentRequestId) && Boolean(key) && key === currentKey
);

export const mergeCheckoutPreviewAddress = ({ requestAddress = {}, previewAddress = {}, currentAddress = {} } = {}) => {
  const previewCep = text(previewAddress.cep || previewAddress.postalCode).replace(/\D/g, '');
  return {
    ...requestAddress,
    ...previewAddress,
    cep: previewCep.length === 8 ? previewCep : text(requestAddress.cep),
    // Referência e observações não participam da geocodificação. Preserve o valor
    // mais recente digitado enquanto a resposta assíncrona estava em voo.
    reference: text(currentAddress.reference),
    notes: text(currentAddress.notes),
  };
};
